import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type Data, DataSet, Network, type Edge as VisEdge, type Node as VisNode } from "vis-network/standalone";
import FilterPanel, { type FilterCriteria } from "@/features/graph/components/controls/FilterPanel";
import InformationPanel from "@/features/graph/components/controls/InformationPanel";
import StatisticsPanel, { type GraphStatistics } from "@/features/graph/components/controls/StatisticsPanel";
import NodeHoverTooltip from "@/features/graph/components/NodeHoverTooltip";
import useGraphFilter from "@/features/graph/hooks/useGraphFilter";
import useGraphMetrics from "@/features/graph/hooks/useGraphMetrics";
import useGraphSelectionSync from "@/features/graph/hooks/useGraphSelectionSync";
import { useNodeHoverTooltip } from "@/features/graph/hooks/useNodeHoverTooltip";
import { computeNodeColors, useVisNetworkBuilders } from "@/features/graph/hooks/useVisNetworkBuilders";
import type {
	KnowledgeGraphVisualization as KnowledgeGraphVisualizationType,
	VisualEdge,
	VisualNode,
} from "@/types/knowledgeGraph";

export interface GraphHighlightHandle {
	highlightNodes: (nodeIds: string[], options?: { pulse?: boolean; color?: string }) => void;
	highlightEdges: (edgeIds: string[]) => void;
	clearHighlights: () => void;
}

export interface KnowledgeGraphVisualizationProps {
	data: KnowledgeGraphVisualizationType;
	onNodeClick?: (node: VisualNode) => void;
	onEdgeClick?: (edge: VisualEdge) => void;
	onBackgroundClick?: () => void;
	selectedNodeId?: string | null;
	selectedEdgeId?: string | null;
	graphId?: string | null;
	showControls?: boolean;
	onStatisticsChange?: (stats: GraphStatistics) => void;
	onNodesEdgesChange?: (nodes: VisualNode[], edges: VisualEdge[]) => void;
	onFilterChange?: (filter: FilterCriteria) => void;
	onResetFilter?: () => void;
	filterRef?: React.MutableRefObject<((filter: FilterCriteria) => void) | null>;
	resetFilterRef?: React.MutableRefObject<(() => void) | null>;
	isEditing?: boolean;
	onNodeDelete?: (nodeId: string) => void;
	deleteNodeRef?: React.MutableRefObject<((nodeId: string) => void) | null>;
	graphHighlightRef?: React.MutableRefObject<GraphHighlightHandle | null>;
}

function calculateInitialPositions(
	nodesToPosition: VisualNode[],
	communities: Map<string, number>,
	degree: Map<string, number>,
): Map<string, { x: number; y: number }> {
	const nodesByCommunity = new Map<number, VisualNode[]>();
	nodesToPosition.forEach((node) => {
		const communityId = communities.get(node.id) ?? 0;
		if (!nodesByCommunity.has(communityId)) {
			nodesByCommunity.set(communityId, []);
		}
		nodesByCommunity.get(communityId)?.push(node);
	});

	const communityCount = nodesByCommunity.size;
	const positions = new Map<string, { x: number; y: number }>();

	if (communityCount === 0) return positions;

	const baseRadius = Math.max(200, Math.sqrt(communityCount) * 150);
	const centerX = 0;
	const centerY = 0;

	Array.from(nodesByCommunity.entries()).forEach(([, communityNodes], index) => {
		const angle = (index * 2 * Math.PI) / communityCount;
		const clusterCenterX = centerX + baseRadius * Math.cos(angle);
		const clusterCenterY = centerY + baseRadius * Math.sin(angle);

		const nodeCount = communityNodes.length;
		const clusterRadius = Math.max(50, Math.sqrt(nodeCount) * 30);

		const sortedNodes = [...communityNodes].sort((a, b) => (degree.get(b.id) || 0) - (degree.get(a.id) || 0));
		const maxDegree = Math.max(...Array.from(degree.values()), 1);

		sortedNodes.forEach((node, nodeIndex) => {
			const nodeAngle = (nodeIndex * 2 * Math.PI) / nodeCount;
			const degreeRatio = (degree.get(node.id) || 1) / maxDegree;
			const nodeRadius = clusterRadius * (0.5 + degreeRatio * 0.3);
			positions.set(node.id, {
				x: clusterCenterX + nodeRadius * Math.cos(nodeAngle),
				y: clusterCenterY + nodeRadius * Math.sin(nodeAngle),
			});
		});
	});

	return positions;
}

const KnowledgeGraphVisualization: React.FC<KnowledgeGraphVisualizationProps> = ({
	data,
	onNodeClick,
	onEdgeClick,
	onBackgroundClick,
	selectedNodeId,
	selectedEdgeId,
	showControls = true,
	onStatisticsChange,
	onNodesEdgesChange,
	onFilterChange,
	onResetFilter,
	filterRef,
	resetFilterRef,
	isEditing = false,
	onNodeDelete,
	deleteNodeRef,
	graphHighlightRef,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const networkRef = useRef<Network | null>(null);
	const nodesDataSetRef = useRef<DataSet<VisNode> | null>(null);
	const edgesDataSetRef = useRef<DataSet<VisEdge> | null>(null);
	const dataNodes = data?.nodes ?? [];
	const dataEdges = data?.edges ?? [];
	const { theme, graphTheme, buildVisNode, buildVisEdge, networkOptions } = useVisNetworkBuilders();
	const { graphMetrics, visualNodes, visualEdges } = useGraphMetrics({ dataNodes, dataEdges });
	const [nodes, setNodes] = useState<VisualNode[]>(visualNodes);
	const [edges, setEdges] = useState<VisualEdge[]>(visualEdges);
	const [statsVisible, setStatsVisible] = useState(true);
	const [filterVisible, setFilterVisible] = useState(true);
	const [infoVisible, setInfoVisible] = useState(true);
	const { filteredNodes, filteredEdges, setFilteredNodes, setFilteredEdges, applyFilter, resetFilter } = useGraphFilter(
		{
			nodes,
			edges,
			onFilterChange,
			onResetFilter,
		},
	);

	const getNodeData = useCallback(
		(nodeId: string) => {
			const node = filteredNodes.find((n) => n.id === nodeId);
			return node ? { name: node.name, elementType: node.elementType, metadata: node.metadata } : null;
		},
		[filteredNodes],
	);
	const { registerHoverEvents, clearHover, hoveredNode, tooltipPos } = useNodeHoverTooltip({
		containerRef,
		getNodeData,
	});

	useEffect(() => {
		setNodes(visualNodes);
		setEdges(visualEdges);
		setFilteredNodes(visualNodes);
		setFilteredEdges(visualEdges);
	}, [visualNodes, visualEdges, setFilteredNodes, setFilteredEdges]);

	// Calculate graph statistics
	const graphStatistics: GraphStatistics = useMemo(() => {
		const nodeCount = nodes.length;
		const edgeCount = edges.length;
		const communityCount = new Set(graphMetrics.communities.values()).size;

		// Calculate average degree
		const totalDegree = Array.from(graphMetrics.degree.values()).reduce((sum, d) => sum + d, 0);
		const averageDegree = nodeCount > 0 ? totalDegree / nodeCount : 0;

		// Calculate density (for undirected graph)
		const maxEdges = (nodeCount * (nodeCount - 1)) / 2;
		const density = maxEdges > 0 ? edgeCount / maxEdges : 0;

		return {
			nodes: nodeCount,
			edges: edgeCount,
			communities: communityCount,
			averageDegree,
			density,
		};
	}, [nodes.length, edges.length, graphMetrics.communities, graphMetrics.degree]);

	// Expose statistics when they change
	useEffect(() => {
		if (onStatisticsChange) {
			onStatisticsChange(graphStatistics);
		}
	}, [graphStatistics, onStatisticsChange]);

	// Expose nodes and edges when they change
	useEffect(() => {
		if (onNodesEdgesChange) {
			onNodesEdgesChange(nodes, edges);
		}
	}, [nodes, edges, onNodesEdgesChange]);

	// Delete node function
	const deleteNode = useCallback(
		(nodeId: string) => {
			if (!nodesDataSetRef.current || !edgesDataSetRef.current) {
				return;
			}

			// Find all edges connected to this node
			const connectedEdgeIds: string[] = [];
			filteredEdges.forEach((edge) => {
				const source = typeof edge.source === "string" ? edge.source : edge.source.id;
				const target = typeof edge.target === "string" ? edge.target : edge.target.id;
				if (source === nodeId || target === nodeId) {
					connectedEdgeIds.push(edge.id);
				}
			});

			// Remove edges first
			edgesDataSetRef.current.remove(connectedEdgeIds);

			// Remove the node
			nodesDataSetRef.current.remove(nodeId);

			// Update local state
			setFilteredNodes((prev) => prev.filter((node) => node.id !== nodeId));
			setFilteredEdges((prev) =>
				prev.filter((edge) => {
					const source = typeof edge.source === "string" ? edge.source : edge.source.id;
					const target = typeof edge.target === "string" ? edge.target : edge.target.id;
					return source !== nodeId && target !== nodeId;
				}),
			);

			// Call the parent callback if provided
			if (onNodeDelete) {
				onNodeDelete(nodeId);
			}
		},
		[filteredEdges, onNodeDelete, setFilteredEdges, setFilteredNodes],
	);

	// Highlight nodes/edges for chat tool feedback
	const originalColorsRef = useRef<Map<string, { background: string; border: string }>>(new Map());
	const highlightedNodeIdsRef = useRef<Set<string>>(new Set());
	const highlightedEdgeIdsRef = useRef<Set<string>>(new Set());

	const highlightNodes = useCallback(
		(nodeIds: string[], options?: { pulse?: boolean; color?: string }) => {
			if (!nodesDataSetRef.current || !edgesDataSetRef.current) return;
			const highlightColor = options?.color ?? "#fbbf24";
			const highlightBorder = "#f59e0b";
			for (const id of nodeIds) {
				const node = filteredNodes.find((n) => n.id === id || n.graphElementId === id);
				if (node) {
					const orig = {
						background: node.color || "#6366f1",
						border: theme === "dark" ? "#1f2937" : "#ffffff",
					};
					originalColorsRef.current.set(node.id, orig);
					highlightedNodeIdsRef.current.add(node.id);
					nodesDataSetRef.current.update({
						id: node.id,
						color: {
							background: highlightColor,
							border: highlightBorder,
							highlight: { background: "#fcd34d", border: "#f59e0b" },
							hover: { background: "#fde68a", border: "#f59e0b" },
						},
					});
				}
			}
		},
		[filteredNodes, theme],
	);

	const highlightEdges = useCallback((edgeIds: string[]) => {
		if (!edgesDataSetRef.current) return;
		for (const id of edgeIds) {
			highlightedEdgeIdsRef.current.add(id);
			edgesDataSetRef.current.update({
				id,
				color: { color: "#fbbf24", highlight: "#f59e0b", opacity: 1 },
			});
		}
	}, []);

	const clearHighlights = useCallback(() => {
		if (!nodesDataSetRef.current || !edgesDataSetRef.current) return;
		for (const nodeId of highlightedNodeIdsRef.current) {
			const orig = originalColorsRef.current.get(nodeId);
			const node = filteredNodes.find((n) => n.id === nodeId);
			if (orig && node) {
				nodesDataSetRef.current.update({
					id: nodeId,
					color: {
						background: orig.background,
						border: orig.border,
						highlight: { background: "#fbbf24", border: "#f59e0b" },
						hover: { background: node.color || "#818cf8", border: theme === "dark" ? "#374151" : "#e5e7eb" },
					},
				});
			}
			originalColorsRef.current.delete(nodeId);
		}
		highlightedNodeIdsRef.current.clear();
		for (const edgeId of highlightedEdgeIdsRef.current) {
			const edge = filteredEdges.find((e) => e.id === edgeId);
			if (edge) {
				const isInferred = edge.inferred || false;
				edgesDataSetRef.current.update({
					id: edgeId,
					color: {
						color: isInferred ? (theme === "dark" ? "#6b7280" : "#9ca3af") : theme === "dark" ? "#9ca3af" : "#6b7280",
						highlight: "#fbbf24",
						opacity: isInferred ? 0.5 : 0.8,
					},
				});
			}
		}
		highlightedEdgeIdsRef.current.clear();
	}, [filteredNodes, filteredEdges, theme]);

	useEffect(() => {
		if (graphHighlightRef) {
			graphHighlightRef.current = { highlightNodes, highlightEdges, clearHighlights };
		}
		return () => {
			if (graphHighlightRef) graphHighlightRef.current = null;
		};
	}, [graphHighlightRef, highlightNodes, highlightEdges, clearHighlights]);

	// Expose filter functions and delete function via refs
	useEffect(() => {
		if (filterRef) {
			filterRef.current = applyFilter;
		}
		if (resetFilterRef) {
			resetFilterRef.current = resetFilter;
		}
		if (deleteNodeRef) {
			deleteNodeRef.current = deleteNode;
		}
	}, [filterRef, resetFilterRef, deleteNodeRef, applyFilter, resetFilter, deleteNode]);

	// Store onNodeClick and onEdgeClick in refs to avoid recreating network when callbacks change
	const onNodeClickRef = useRef(onNodeClick);
	useEffect(() => {
		onNodeClickRef.current = onNodeClick;
	}, [onNodeClick]);

	const onEdgeClickRef = useRef(onEdgeClick);
	useEffect(() => {
		onEdgeClickRef.current = onEdgeClick;
	}, [onEdgeClick]);

	const onBackgroundClickRef = useRef(onBackgroundClick);
	useEffect(() => {
		onBackgroundClickRef.current = onBackgroundClick;
	}, [onBackgroundClick]);

	const nodeDisplayColors = useMemo(
		() => computeNodeColors(filteredNodes, graphTheme.accent, theme),
		[filteredNodes, graphTheme.accent, theme],
	);

	// Initialize vis-network (only recreate when nodes/edges/theme change, NOT on selection change or callback changes)
	useEffect(() => {
		if (!containerRef.current || filteredNodes.length === 0) return;

		// Calculate initial positions based on communities
		const initialPositions = calculateInitialPositions(filteredNodes, graphMetrics.communities, graphMetrics.degree);

		// Build vis-network nodes via shared builder (colour, font, shadow, etc.)
		const radii = filteredNodes.map((n) => n.radius ?? 12);
		const minRadius = filteredNodes.length > 0 ? Math.min(...radii) : 0;
		const maxRadius = filteredNodes.length > 0 ? Math.max(...radii) : 1;
		const spread = maxRadius - minRadius;

		const visNodes = filteredNodes.map((node) => {
			const initialPos = initialPositions.get(node.id);
			const normalizedRadius = spread > 0 ? ((node.radius ?? 12) - minRadius) / spread : 0.5;
			return buildVisNode(node, { pos: initialPos, normalizedRadius });
		});

		// Build vis-network edges via shared builder
		const visEdges = filteredEdges.map((edge) => buildVisEdge(edge));

		// Create DataSets for nodes and edges
		const nodesDataSet = new DataSet(visNodes);
		const edgesDataSet = new DataSet(visEdges);
		nodesDataSetRef.current = nodesDataSet;
		edgesDataSetRef.current = edgesDataSet;

		const visData: Data = {
			nodes: nodesDataSet,
			edges: edgesDataSet,
		};

		// Create network
		const network = new Network(containerRef.current, visData, networkOptions);
		networkRef.current = network;

		registerHoverEvents(network);

		// Click handler: node/edge/background (refs avoid network recreation)
		network.on("click", (params) => {
			if (params.nodes.length > 0) {
				const clickedNodeId = params.nodes[0] as string;
				const clickedNode = filteredNodes.find((n) => n.id === clickedNodeId);
				if (clickedNode && onNodeClickRef.current) {
					onNodeClickRef.current(clickedNode);
				}
			} else if (params.edges.length > 0) {
				const clickedEdgeId = params.edges[0] as string;
				const clickedEdge = filteredEdges.find((e) => e.id === clickedEdgeId);
				if (clickedEdge && onEdgeClickRef.current) {
					onEdgeClickRef.current(clickedEdge);
				}
			} else {
				// Click on empty space - deselect
				if (onBackgroundClickRef.current) {
					onBackgroundClickRef.current();
				}
			}
		});

		// Cleanup
		return () => {
			clearHover();
			if (networkRef.current) {
				networkRef.current.destroy();
				networkRef.current = null;
			}
			nodesDataSetRef.current = null;
			edgesDataSetRef.current = null;
		};
	}, [
		filteredNodes,
		filteredEdges,
		networkOptions,
		buildVisNode,
		buildVisEdge,
		graphMetrics.degree,
		graphMetrics.communities,
		registerHoverEvents,
		clearHover,
	]);

	// Find selected node data
	const selectedNode = useMemo(() => {
		if (!selectedNodeId) return null;
		return filteredNodes.find((n) => n.id === selectedNodeId) || null;
	}, [selectedNodeId, filteredNodes]);

	useGraphSelectionSync({
		selectedNodeId,
		filteredNodes,
		nodesDataSetRef,
		nodeDisplayColors,
		graphTheme,
		theme,
	});

	return (
		<div className="flex h-full w-full">
			{/* Left panel for filters and statistics */}
			{showControls && (
				<div className="flex-shrink-0 w-80 p-4 overflow-y-auto surface-alt border-r border-subtle">
					<div className="mb-4">
						<h2 className="text-lg font-semibold text-primary">Controls</h2>
					</div>

					<StatisticsPanel
						statistics={graphStatistics}
						visible={statsVisible}
						onToggle={() => setStatsVisible(!statsVisible)}
					/>

					<FilterPanel
						nodes={nodes}
						edges={edges}
						onFilter={applyFilter}
						onReset={resetFilter}
						visible={filterVisible}
						onToggle={() => setFilterVisible(!filterVisible)}
					/>

					<InformationPanel
						selectedNode={selectedNode}
						selectedEdge={selectedEdgeId ? filteredEdges.find((e) => e.id === selectedEdgeId) || null : null}
						visible={infoVisible}
						onToggle={() => setInfoVisible(!infoVisible)}
					/>
				</div>
			)}

			{/* Graph visualization area - takes up remaining space */}
			<div
				className={`flex-1 h-full w-full rounded-lg surface ${isEditing ? "cursor-pointer" : "cursor-default"}`}
				ref={containerRef}
			/>

			{hoveredNode && tooltipPos && (
				<NodeHoverTooltip
					name={hoveredNode.name}
					elementType={hoveredNode.elementType}
					metadata={hoveredNode.metadata}
					position={tooltipPos}
				/>
			)}
		</div>
	);
};

export default KnowledgeGraphVisualization;
